import Tour from "reactour";
import type { ReactourStep } from "reactour";
import { useEffect, useState } from "react";
import type { IUserOpenedTutorial } from "../../model/model";
import { ArrowRightIcon, ArrowLeftIcon } from "@heroicons/react/outline";

import {
  getUserOpenedTutorial,
  setHasOpenedTutorial,
} from "../../features/profile/state/profileSlice";
import {
  closeOnboardingModal,
  getCurrentOnboardingModalState,
  openOnboardingModal,
} from "../state/slice/modal/modalSlice";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { useTranslation } from "react-i18next";

interface OnboardingProps {
  steps: ReactourStep[];
  relevantArticle: string;
  id: string;
}

const OnboardingComponent = ({ steps, relevantArticle, id }: OnboardingProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const userOpenedTutorials = useAppSelector(getUserOpenedTutorial) as IUserOpenedTutorial;
  const onboardingState = useAppSelector(getCurrentOnboardingModalState);
  const dbKeyname = `web_${relevantArticle}`;

  const handleTourClose = () => {
    setCurrentStep(0);
    dispatch(closeOnboardingModal(id));
    if (userOpenedTutorials?.id) {
      void dispatch(
        setHasOpenedTutorial({
          userOpenedTutorialId: Number(userOpenedTutorials.id),
          newValues: {
            ...userOpenedTutorials,
            [dbKeyname]: true,
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (!relevantArticle || relevantArticle.trim() === "") {
      console.log("[OnBoardingComponent] Skipping - no relevantArticle provided");
      return;
    }

    console.log("[OnBoardingComponent] useEffect triggered", {
      relevantArticle,
      dbKeyname,
      userOpenedTutorials,
      hasId: !!userOpenedTutorials?.id,
      flagValue: userOpenedTutorials?.[dbKeyname as keyof IUserOpenedTutorial],
    });

    if (userOpenedTutorials?.id) {
      const shouldPopHelpIntoUserFace =
        !userOpenedTutorials[dbKeyname as keyof IUserOpenedTutorial];
      console.log("[OnBoardingComponent] Checking if should show tutorial", {
        dbKeyname,
        flagValue: userOpenedTutorials[dbKeyname as keyof IUserOpenedTutorial],
        shouldPopHelpIntoUserFace,
      });

      if (shouldPopHelpIntoUserFace) {
        console.log("[OnBoardingComponent] OPENING tutorial modal for", relevantArticle);
        dispatch(openOnboardingModal(relevantArticle));
      } else {
        console.log("[OnBoardingComponent] NOT showing tutorial - flag is true (dismissed)");
      }
    } else {
      console.log("[OnBoardingComponent] userOpenedTutorials not loaded yet, skipping");
    }
  }, [userOpenedTutorials, dbKeyname, relevantArticle, dispatch]);

  return (
    <Tour
      getCurrentStep={(step) => {
        setCurrentStep(step);
      }}
      rounded={5}
      goToStep={currentStep}
      steps={steps}
      isOpen={onboardingState.id === id ? onboardingState.show : false}
      onRequestClose={handleTourClose}
      maskSpace={5}
      accentColor="#00c889"
      prevButton={
        <button className="btn btn-primary">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
      }
      nextButton={
        <button className="btn btn-primary">
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      }
      lastStepNextButton={
        <button className="btn-primary-fill w-full text-base" onClick={() => handleTourClose()}>
          {t("general.finish")}
        </button>
      }
    />
  );
};

export default OnboardingComponent;
